import React, { useEffect, useMemo, useState } from 'react';
import { Empty, Input, Modal, Select, Tooltip } from 'antd';
import { showToast } from 'utils/helpers';
import { createMailGroup, deleteMailGroup, updateMailGroup } from '../helpers/call-api';
import { formatUserLabel } from '../utils/userLabel';

const normalizeGroupUsers = (users = []) => {
  return (users || [])
    .map((item) => item?.user_name || item?.username || item)
    .filter(Boolean)
    .map((userName) => `USER-${userName}`);
};

const MailGroupModal = ({ open, onClose, userOptions = [], groups = [], onSaved }) => {
  const [editingGroup, setEditingGroup] = useState(null);
  const [groupName, setGroupName] = useState('');
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [submitting, setSubmitting] = useState(false);

  const userSelectOptions = useMemo(() => {
    return (userOptions || []).map((item) => ({
      value: item.value,
      label: item.label,
    }));
  }, [userOptions]);

  useEffect(() => {
    if (!open) {
      setEditingGroup(null);
      setGroupName('');
      setSelectedUsers([]);
    }
  }, [open]);

  const resetForm = () => {
    setEditingGroup(null);
    setGroupName('');
    setSelectedUsers([]);
  };

  const handleEdit = (group) => {
    setEditingGroup(group);
    setGroupName(group?.group_name || '');
    setSelectedUsers(normalizeGroupUsers(group?.users || []));
  };

  const handleSubmit = async () => {
    const payload = {
      group_name: groupName.trim(),
      user_names: selectedUsers.map((item) => String(item).replace(/^USER-/, '')),
    };

    if (!payload.group_name) {
      showToast.error('Tên nhóm là bắt buộc.');
      return;
    }

    if (!payload.user_names.length) {
      showToast.error('Nhóm phải có ít nhất một người dùng.');
      return;
    }

    try {
      setSubmitting(true);
      if (editingGroup?.mail_group_id) {
        await updateMailGroup(editingGroup.mail_group_id, payload);
      } else {
        await createMailGroup(payload);
      }
      showToast.success(`${editingGroup?.mail_group_id ? 'Cập nhật' : 'Tạo'} nhóm thành công.`);
      resetForm();
      await onSaved?.();
    } catch (error) {
      showToast.error(error?.message || 'Có lỗi xảy ra!');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = (group) => {
    Modal.confirm({
      title: 'Xóa nhóm người nhận',
      content: `Bạn có chắc muốn xóa nhóm "${group?.group_name}"?`,
      okText: 'Xóa',
      cancelText: 'Hủy',
      okButtonProps: { danger: true },
      onOk: async () => {
        try {
          await deleteMailGroup(group.mail_group_id);
          showToast.success('Xóa nhóm thành công.');
          if (editingGroup?.mail_group_id === group.mail_group_id) {
            resetForm();
          }
          await onSaved?.();
        } catch (error) {
          showToast.error(error?.message || 'Có lỗi xảy ra!');
        }
      },
    });
  };

  return (
    <Modal
      open={open}
      title='Nhóm người nhận'
      onCancel={onClose}
      width={780}
      footer={[
        <button key='close' className='bw_btn_outline bw_btn_outline_danger' type='button' onClick={onClose}>
          Đóng
        </button>,
        <button
          key='save'
          className='bw_btn bw_btn_success'
          type='button'
          disabled={submitting}
          onClick={handleSubmit}>
          {editingGroup?.mail_group_id ? 'Cập nhật nhóm' : 'Tạo nhóm'}
        </button>,
      ]}>
      <div className='bw_row'>
        <div className='bw_col_12 bw_mb_2'>
          <label className='bw_label'>Tên nhóm</label>
          <Input value={groupName} onChange={(event) => setGroupName(event.target.value)} placeholder='Nhập tên nhóm' />
        </div>
        <div className='bw_col_12 bw_mb_2'>
          <label className='bw_label'>Người dùng trong nhóm</label>
          <Select
            mode='multiple'
            allowClear
            showSearch
            value={selectedUsers}
            options={userSelectOptions}
            optionFilterProp='label'
            onChange={setSelectedUsers}
            style={{ width: '100%' }}
            placeholder='Chọn người dùng'
            maxTagCount='responsive'
          />
        </div>
      </div>

      <div className='bw_mt_2'>
        {(groups || []).length ? (
          <div className='bw_table_responsive'>
            <table className='bw_table'>
              <thead>
                <tr>
                  <th>Tên nhóm</th>
                  <th>Thành viên</th>
                  <th style={{ width: 110 }}>Thao tác</th>
                </tr>
              </thead>
              <tbody>
                {groups.map((group) => (
                  <tr key={group.mail_group_id}>
                    <td>{group.group_name}</td>
                    <td>
                      <Tooltip
                        title={(group.users || [])
                          .map((user) => formatUserLabel(user.user_name, user.full_name))
                          .join(', ')}>
                        <span>{group.total_user || 0} người dùng</span>
                      </Tooltip>
                    </td>
                    <td>
                      <button type='button' className='bw_btn_table' onClick={() => handleEdit(group)}>
                        <i className='fi fi-rr-edit' />
                      </button>
                      <button type='button' className='bw_btn_table bw_red' onClick={() => handleDelete(group)}>
                        <i className='fi fi-rr-trash' />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <Empty description='Chưa có nhóm người nhận' />
        )}
      </div>
    </Modal>
  );
};

export default MailGroupModal;
